import { Component, computed, input, InputSignal, output, OutputEmitterRef, viewChild } from '@angular/core';
import { FieldTree, FormField } from '@angular/forms/signals';
import { MatError } from '@angular/material/form-field';
import { TimeButtonsComponent } from '@app/components/time-reports/time-report-dialog/time-buttons/time-buttons.component';
import { TimePickerValue } from '@app/shared/types/models/shared/time-picker-value';
import { TimeReportingType } from '@app/shared/types/models/shared/time-reporting-type';
import { UiTimePickerComponent } from '@app/shared/ui/time-picker/ui-time-picker.component';
import { TranslatePipe } from '@ngx-translate/core';
import { TimeRangeModel } from '../../time-report-form.types';

@Component({
  selector: 'proffeo-time-input-section',
  templateUrl: './time-input-section.component.html',
  imports: [FormField, MatError, TranslatePipe, UiTimePickerComponent, TimeButtonsComponent]
})
export class TimeInputSectionComponent {
  private readonly secondTimePicker = viewChild<UiTimePickerComponent>('secondTimePicker');

  protected readonly TimeReportingType: typeof TimeReportingType = TimeReportingType;

  /** The time picker draws the outline itself; the message below it appears once the field is touched. */
  protected readonly showManualDurationError = computed((): boolean => {
    const manualTime = this.manualTime()();
    return manualTime.touched() && manualTime.errors().some(error => error.kind === 'invalidManualTime');
  });

  protected readonly showTimeRangeError = computed((): boolean => {
    const timeRange = this.timeRange()();
    return timeRange.touched() && timeRange.errorSummary().some(error => error.kind === 'invalidTimeRange');
  });

  protected readonly rangeMinutes = computed((): number => {
    const { from, to } = this.timeRange()().value();
    const diffMinutes = to.hours * 60 + to.minutes - (from.hours * 60 + from.minutes);
    return diffMinutes < 0 ? diffMinutes + 24 * 60 : diffMinutes;
  });

  protected readonly durationHoursDisplay = computed((): string =>
    this.formatValue(Math.floor(this.rangeMinutes() / 60))
  );

  protected readonly durationMinutesDisplay = computed((): string => this.formatValue(this.rangeMinutes() % 60));

  protected readonly durationDisplay = computed(
    (): string => `${this.durationHoursDisplay()} : ${this.durationMinutesDisplay()}`
  );

  public readonly mode: InputSignal<TimeReportingType> = input.required<TimeReportingType>();
  public readonly manualTime: InputSignal<FieldTree<TimePickerValue>> = input.required<FieldTree<TimePickerValue>>();
  public readonly timeRange: InputSignal<FieldTree<TimeRangeModel>> = input.required<FieldTree<TimeRangeModel>>();
  public readonly minuteStep: InputSignal<number> = input<number>(1);
  public readonly quickTimeSelected: OutputEmitterRef<TimePickerValue> = output<TimePickerValue>();

  protected onQuickTimeSelected(value: TimePickerValue): void {
    this.quickTimeSelected.emit(value);
  }

  protected onFirstTimePickerUpdated(): void {
    this.secondTimePicker()?.focusHours();
  }

  private formatValue(value?: number): string {
    if (!value) {
      return '00';
    }
    return value < 10 ? `0${value}` : value.toString();
  }
}
